import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
} from '../services/api';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import toast from 'react-hot-toast';

const emptyForm = { title: '', body: '', for_shops: true, for_factories: true, is_pinned: false };

// The sidebar shows a preview of the latest announcements. It normally only
// refreshes on navigation or on a slow timer, which misses changes made while
// already sitting on this page — so tell it directly.
const notifySidebar = () => {
  try { window.dispatchEvent(new Event('announcements-changed')); } catch (e) { /* ignore */ }
};

export default function Announcements() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await getAnnouncements({ limit: 100, includeInactive: isAdmin && showInactive });
      setItems(r.data);
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.error || 'Σφάλμα φόρτωσης');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isAdmin, showInactive]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title,
      body: a.body,
      for_shops: a.for_shops,
      for_factories: a.for_factories,
      is_pinned: a.is_pinned,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error('Συμπληρώστε τίτλο');
    if (!form.body.trim()) return toast.error('Συμπληρώστε κείμενο');
    if (!form.for_shops && !form.for_factories) {
      return toast.error('Επιλέξτε τουλάχιστον έναν παραλήπτη');
    }
    setSaving(true);
    try {
      if (editing) {
        await updateAnnouncement(editing.id, form);
        toast.success('Ενημερώθηκε');
      } else {
        await createAnnouncement(form);
        toast.success('Δημοσιεύτηκε');
      }
      setShowModal(false);
      load(true);
      notifySidebar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setSaving(false);
    }
  };

  const retire = async (a) => {
    if (!window.confirm(`Απόσυρση ανακοίνωσης: ${a.title};`)) return;
    try { await deleteAnnouncement(a.id, false); toast.success('Αποσύρθηκε'); load(true); notifySidebar(); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };
  const restore = async (a) => {
    try { await updateAnnouncement(a.id, { is_active: true }); toast.success('Επαναφέρθηκε'); load(true); notifySidebar(); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };
  const hardDelete = async (a) => {
    if (!window.confirm(`ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ: ${a.title};\nΔεν αναιρείται.`)) return;
    try { await deleteAnnouncement(a.id, true); toast.success('Διαγράφηκε'); load(true); notifySidebar(); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };

  const fmt = (iso) => format(new Date(iso), 'd MMM yyyy, HH:mm', { locale: el });

  const audienceLabel = (a) => {
    if (a.for_shops && a.for_factories) return 'Όλοι';
    if (a.for_shops) return 'Καταστήματα';
    if (a.for_factories) return 'Εργοστάσια';
    return '—';
  };

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h2>📢 Ανακοινώσεις</h2>
            <p>{isAdmin ? 'Σημειώσεις προς καταστήματα και εργοστάσια' : 'Ανακοινώσεις από τη διοίκηση'}</p>
          </div>
          <div className="flex-row">
            {isAdmin && (
              <label style={{ fontSize: 13, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                Αποσυρμένες
              </label>
            )}
            <button className="btn btn-secondary" onClick={() => load()} title="Ανανέωση">↻</button>
            {isAdmin && <button className="btn btn-primary" onClick={openCreate}>+ Νέα Ανακοίνωση</button>}
          </div>
        </div>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="page-loading"><span className="loading-spinner dark" /></div>
        ) : items.length === 0 ? (
          <div className="card"><div className="card-body">
            <div className="empty-state"><div>📢</div><p>Δεν υπάρχουν ανακοινώσεις</p></div>
          </div></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((a) => (
              <div key={a.id} className="card" style={{ opacity: a.is_active ? 1 : 0.6 }}>
                <div className="card-body">
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ fontSize: 20, lineHeight: 1.2 }}>{a.is_pinned ? '📌' : '📢'}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--gray-900)' }}>{a.title}</span>
                        {isAdmin && (
                          <span className="badge badge-blue" style={{ fontSize: 10 }}>{audienceLabel(a)}</span>
                        )}
                        {!a.is_active && (
                          <span className="badge badge-gray" style={{ fontSize: 10 }}>αποσυρμένη</span>
                        )}
                      </div>
                      <div style={{
                        marginTop: 8, fontSize: 14, color: 'var(--gray-700)',
                        lineHeight: 1.55, whiteSpace: 'pre-wrap',
                      }}>
                        {a.body}
                      </div>
                      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--gray-400)' }}>
                        {fmt(a.created_at)}
                        {a.created_by_name && ` · ${a.created_by_name}`}
                        {a.updated_at && a.updated_at !== a.created_at && ` · επεξεργασία ${fmt(a.updated_at)}`}
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex-row" style={{ flexShrink: 0 }}>
                        {a.is_active ? (
                          <>
                            <button className="btn btn-secondary btn-sm" onClick={() => openEdit(a)}>✏️</button>
                            <button className="btn btn-danger btn-sm" onClick={() => retire(a)} title="Απόσυρση">🗑</button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-secondary btn-sm" onClick={() => restore(a)} title="Επαναφορά">↻</button>
                            <button className="btn btn-danger btn-sm" onClick={() => hardDelete(a)} title="Οριστική διαγραφή">❌</button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontWeight: 600 }}>{editing ? 'Επεξεργασία Ανακοίνωσης' : 'Νέα Ανακοίνωση'}</span>
              <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              <div className="form-group">
                <label className="form-label">Τίτλος *</label>
                <input className="form-input" value={form.title} autoFocus
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Κείμενο *</label>
                <textarea className="form-textarea" rows="7" value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Ποιοι τη βλέπουν *</label>
                <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 8px' }}>
                  Οι διαχειριστές βλέπουν πάντα όλες τις ανακοινώσεις.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--gray-200)', borderRadius: 8, padding: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                    <input type="checkbox" checked={form.for_shops}
                      onChange={(e) => setForm((f) => ({ ...f, for_shops: e.target.checked }))} />
                    🏪 Καταστήματα
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                    <input type="checkbox" checked={form.for_factories}
                      onChange={(e) => setForm((f) => ({ ...f, for_factories: e.target.checked }))} />
                    🏭 Εργοστάσια
                  </label>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input type="checkbox" checked={form.is_pinned}
                    onChange={(e) => setForm((f) => ({ ...f, is_pinned: e.target.checked }))} />
                  📌 Καρφίτσωμα στην κορυφή
                </label>
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Άκυρο</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <span className="loading-spinner" /> : 'Αποθήκευση'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
